"use client";

import { useEffect, useState } from "react";

export default function TableOfContents({ sections }) {
  const tocItems = [];
  sections.forEach((section, index) => {
    tocItems.push({
      id: `section-${index}`,
      title: section.heading,
      level: 2,
    });
    if (section.subsections) {
      section.subsections.forEach((subsection, subIndex) => {
        tocItems.push({
          id: `section-${index}-sub-${subIndex}`,
          title: subsection.heading,
          level: 3,
        });
      });
    }
  });

  const firstId = tocItems[0]?.id || "";
  const [activeId, setActiveId] = useState(firstId);

  useEffect(() => {
    if (firstId) setActiveId(firstId);

    const timer = setTimeout(() => {
      const headings = document.querySelectorAll("h2[id], h3[id]");
      const headingElements = Array.from(headings);

      const observer = new IntersectionObserver(
        (entries) => {
          if (window.scrollY < 50) {
            setActiveId(headingElements[0]?.id || firstId);
            return;
          }
          const intersectingEntries = entries.filter((e) => e.isIntersecting);
          if (intersectingEntries.length > 0) {
            const sorted = intersectingEntries.sort(
              (a, b) =>
                a.target.getBoundingClientRect().top -
                b.target.getBoundingClientRect().top,
            );
            setActiveId(sorted[0].target.id);
          } else {
            const above = headingElements.filter((h) => {
              const rect = h.getBoundingClientRect();
              return rect.top < 150;
            });
            if (above.length > 0) setActiveId(above[above.length - 1].id);
            else if (headingElements[0]) setActiveId(headingElements[0].id);
          }
        },
        { rootMargin: "-80px 0px -80% 0px", threshold: [0, 0.1, 0.2, 0.5, 1] },
      );

      headingElements.forEach((h) => observer.observe(h));
      return () => observer.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, [sections, firstId]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 100;
      const pos =
        el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: pos, behavior: "smooth" });
      setActiveId(id);
    }
  };

  if (tocItems.length === 0) return null;

  return (
    <div className="docs-toc">
      <div className="overflow-y-auto max-h-[calc(100vh-8rem)]">
        <div className="pb-4">
          <h4 className="text-sm font-medium text-[#171717] mb-4 sentient-medium">
            On This Page
          </h4>
          <nav>
            <ul className="space-y-1 text-xs">
              {tocItems.map((item) => (
                <li key={item.id} className={item.level === 3 ? "ml-3" : ""}>
                  <button
                    data-toc-id={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`text-left w-full py-1 px-3 border-l-2 rounded-r-md transition-colors ${
                      activeId === item.id
                        ? "text-[#171717] font-medium border-[#171717] bg-neutral-100"
                        : "text-[#525252] hover:text-[#171717] border-transparent hover:border-[#E5E5E5] hover:bg-neutral-100"
                    }`}
                  >
                    <span className="line-clamp-2 geist-regular">
                      {item.title}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
}
